import { AuthService, type AuthToken } from '@/services/core/auth'

const API_BASE = import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'
const WEBAUTHN_BASE = `${API_BASE}/v1/webauthn`

interface CredentialDescriptorJson {
  id: string
  type: PublicKeyCredentialType
  transports?: AuthenticatorTransport[]
}

interface RequestOptionsJson extends Omit<PublicKeyCredentialRequestOptions, 'challenge' | 'allowCredentials'> {
  challenge: string
  allowCredentials?: CredentialDescriptorJson[]
}

interface AuthenticationOptionsResponse {
  requestId: string
  publicKey: RequestOptionsJson
}

/** Thrown when the user dismisses the browser passkey prompt — not an error worth surfacing. */
export class PasskeyCancelledError extends Error {}

function b64urlToBytes(b64u: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64u.length % 4)) % 4)
  const bin = atob((b64u + pad).replace(/-/g, '+').replace(/_/g, '/'))
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes
}

function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let s = ''
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function prepareRequestOptions(publicKey: RequestOptionsJson): PublicKeyCredentialRequestOptions {
  return {
    ...publicKey,
    challenge: b64urlToBytes(publicKey.challenge),
    allowCredentials: publicKey.allowCredentials?.map((c) => ({ ...c, id: b64urlToBytes(c.id) })),
  }
}

async function postJson<T>(url: string, body: unknown, fallbackMessage: string): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    let message = fallbackMessage
    try {
      const err = await response.json()
      message = err?.text || err?.error_description || err?.error || message
    } catch { /* ignore */ }
    throw new Error(message)
  }
  return response.json()
}

export const WebAuthnService = {
  isSupported: (): boolean => typeof window !== 'undefined' && !!window.PublicKeyCredential,

  /**
   * Signs in with a passkey. Username is optional — without it the browser offers
   * every discoverable passkey for this site. masqueradeAsUser mirrors the password grant.
   */
  login: async (username?: string, masqueradeAsUser?: string): Promise<AuthToken> => {
    const options = await postJson<AuthenticationOptionsResponse>(
      `${WEBAUTHN_BASE}/authentication/options`,
      { username: username || null },
      'Passkey authentication failed',
    )

    let credential: Credential | null
    try {
      credential = await navigator.credentials.get({ publicKey: prepareRequestOptions(options.publicKey) })
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') throw new PasskeyCancelledError()
      throw err
    }
    if (!credential) throw new PasskeyCancelledError()

    const cred = credential as PublicKeyCredential
    const resp = cred.response as AuthenticatorAssertionResponse & { getTransports?: () => string[] }

    const token = await postJson<AuthToken>(
      `${WEBAUTHN_BASE}/authentication/result?requestId=${encodeURIComponent(options.requestId)}`,
      {
        requestId: options.requestId,
        masqueradeAsUser: masqueradeAsUser || null,
        credential: {
          id: cred.id,
          rawId: bufToB64url(cred.rawId),
          type: cred.type,
          response: {
            clientDataJSON: bufToB64url(resp.clientDataJSON),
            authenticatorData: bufToB64url(resp.authenticatorData),
            signature: bufToB64url(resp.signature),
            userHandle: resp.userHandle ? bufToB64url(resp.userHandle) : null,
          },
          transports: resp.getTransports?.() ?? [],
        },
      },
      'Passkey authentication failed',
    )
    AuthService.storeToken(token)
    return token
  },
}
